import { haversineKm } from "./vworld";
import type { Student } from "@/types";
import { classifyLine, LinePattern } from "./apartmentLines";
import { encodeApartmentId } from "./apartmentInfer";

export interface Stop {
  student: Student;
  lat: number;
  lng: number;
}

/**
 * 전체 stop을 하나로 놓고 최단 동선을 산출(법정동 묶음 없이 글로벌 최적).
 * 같은 건물은 좌표가 같아 자연히 연속 방문된다.
 */
export function optimizeRoute(origin: { lat: number; lng: number }, stops: Stop[]): Stop[] {
  return globalOptimize(origin, stops);
}

/** origin 기준 nearest-neighbor + 2-opt(경계 edge delta, O(n²)/pass)로 전체 최단 동선. */
function globalOptimize(origin: { lat: number; lng: number }, stops: Stop[]): Stop[] {
  const n = stops.length;
  if (n <= 2) return stops.slice();
  const order = optimizeIndices(
    n,
    (i) => haversineKm(origin, stops[i]),
    (a, b) => haversineKm(stops[a], stops[b]),
  );
  return order.map((i) => stops[i]);
}

/**
 * 거리함수 주입형 NN + 2-opt(오픈 투어 — 창고 복귀 없음). 노드 인덱스 순열을 반환.
 *   od(i)   : origin → 노드 i 거리
 *   d(a, b) : 노드 a → 노드 b 거리
 * 직선거리든 도로거리 행렬이든 같은 알고리즘으로 돌린다.
 */
function optimizeIndices(
  n: number,
  od: (i: number) => number,
  d: (a: number, b: number) => number,
): number[] {
  if (n <= 1) return n === 1 ? [0] : [];
  // nearest-neighbor 초기 투어
  const used = new Array(n).fill(false);
  const order: number[] = [];
  let cur = -1; // -1 = origin
  for (let k = 0; k < n; k++) {
    let best = -1, bd = Infinity;
    for (let i = 0; i < n; i++) {
      if (used[i]) continue;
      const dd = cur < 0 ? od(i) : d(cur, i);
      if (dd < bd) { bd = dd; best = i; }
    }
    used[best] = true;
    order.push(best);
    cur = best;
  }
  // 2-opt — 교차/되돌아오는 동선 제거
  let improved = true, iter = 0;
  while (improved && iter < 40) {
    improved = false;
    iter++;
    for (let a = 0; a < n - 1; a++) {
      for (let b = a + 1; b < n; b++) {
        const A = a > 0 ? order[a - 1] : -1;
        const B = order[a], C = order[b], D = b + 1 < n ? order[b + 1] : -1;
        const before = (A < 0 ? od(B) : d(A, B)) + (D < 0 ? 0 : d(C, D));
        const after = (A < 0 ? od(C) : d(A, C)) + (D < 0 ? 0 : d(B, D));
        if (after + 1e-9 < before) {
          for (let l = a, r = b; l < r; l++, r--) { const t = order[l]; order[l] = order[r]; order[r] = t; }
          improved = true;
        }
      }
    }
  }
  return order;
}

/**
 * 단지 하나로 모아도 무리 없는 최대 반경(km). 단지 내 가장 먼 두 가구 거리가
 * 이 값을 넘으면 "초대단지"로 보고 모으지 않는다 — 동끼리 멀리 떨어진 큰 단지는
 * 좌표 최단동선 순서를 그대로 두는 게 유리하기 때문.
 */
const LARGE_COMPLEX_KM = 0.5;

/**
 * 같은 아파트 단지는 좌표가 흩어져 있어도 한 묶음으로 모아 연속 배치.
 *
 * 단지 식별은 단지명(gu·dong·buildingName)으로 한다 — 좌표 인접에 의존하지 않으므로
 * 지오코딩이 단지 안에서 갈라져도(도로명/지번 혼재, provider 폴백 등) 단지가 쪼개지지 않는다.
 * 단지의 경로상 위치는 "그 단지가 처음 등장한 자리"로 고정해 단지 간 큰 동선은 유지한다.
 *
 * 예외: 단지 반경이 LARGE_COMPLEX_KM 를 넘는 초대단지는 모으지 않고 좌표 순서 유지.
 *
 * 단지 내부 정렬 우선순위 (패턴 맵이 있으면 라인 단위까지):
 *   1. 건물 동 번호 (1동, 2동, ... 오름차순)
 *   2. 라인 그룹 인덱스 (1·2호 라인 → 3·4호 라인 순)
 *   3. 호수의 층 (높은 층 → 낮은 층)
 *   4. 호수 단위 (동일 층 안에서)
 *
 * buildingName 이 없는 집(단독주택 등)은 최단동선 위치를 그대로 둔다.
 */
/**
 * 단지 식별 키 함수와 "초대단지(반경 큰 단지)" 집합을 만든다.
 * packApartmentOrder(내부 정렬)와 clusterStops(행렬 노드 묶기)가 같은 기준을 쓰도록 공유.
 *
 * 단지 식별: 단지명(gu·dong·buildingName)이 있으면 그걸로, 없으면 같은 도로명의 단지로,
 * 그것도 없으면 도로명 자체로 묶는다(일부만 buildingName 추출된 경우도 한 단지로 합치기).
 * 단지 반경이 LARGE_COMPLEX_KM 를 넘으면 초대단지로 보고 모으기 대상에서 제외.
 */
function buildClusterKeys(stops: Stop[]): {
  aptKey: (s: Stop["student"]) => string;
  largeComplex: Set<string>;
  groupKey: (s: Stop["student"]) => string;
} {
  // 도로명+건물번호 추출 (예: "부산광역시 해운대구 반여로 67").
  const roadOf = (s: Stop["student"]) =>
    (s.refinedAddress || s.rawAddress || "").split(",")[0].replace(/\s+/g, " ").trim();

  // 도로명(건물번호 포함)별 대표 단지키. 같은 도로명이면 단지명 표기가 갈려도
  // ("금정산 2차 쌍용 예가" vs "금정산2차쌍용예가") 한 단지로 묶는다 — 표기는 최빈값을 대표로.
  const roadNames = new Map<string, Map<string, { count: number; gu: string; dong: string }>>();
  for (const st of stops) {
    const s = st.student;
    if (!s.buildingName) continue;
    const road = roadOf(s);
    if (!road) continue;
    const m = roadNames.get(road) ?? roadNames.set(road, new Map()).get(road)!;
    const e = m.get(s.buildingName) ?? { count: 0, gu: s.gu, dong: s.dong };
    e.count++;
    m.set(s.buildingName, e);
  }
  const roadToApt = new Map<string, string>();
  for (const [road, names] of roadNames) {
    let best = "", bestE = { count: -1, gu: "", dong: "" };
    for (const [name, e] of names) if (e.count > bestE.count) { best = name; bestE = e; }
    roadToApt.set(road, encodeApartmentId(bestE.gu, bestE.dong, best));
  }

  const aptKey = (s: Stop["student"]) => {
    const road = roadOf(s);
    // 같은 도로명에 단지명을 가진 집이 하나라도 있으면 그 대표키로 통합(표기 차이 흡수).
    if (road && roadToApt.has(road)) return roadToApt.get(road)!;
    if (s.buildingName) return encodeApartmentId(s.gu, s.dong, s.buildingName);
    if (!road) return "";
    return `road:${road}`;
  };

  const members = new Map<string, number[]>();
  stops.forEach((s, idx) => {
    const k = aptKey(s.student);
    if (k) (members.get(k) ?? members.set(k, []).get(k)!).push(idx);
  });
  const largeComplex = new Set<string>();
  for (const [k, idxs] of members) {
    if (idxs.length < 2) continue;
    let maxKm = 0;
    for (let a = 0; a < idxs.length; a++) {
      for (let b = a + 1; b < idxs.length; b++) {
        const d = haversineKm(stops[idxs[a]], stops[idxs[b]]);
        if (d > maxKm) maxKm = d;
      }
    }
    if (maxKm > LARGE_COMPLEX_KM) largeComplex.add(k);
  }

  // 묶음 단위 키. 일반 단지는 단지 전체를 한 묶음으로,
  // 초대단지(반경 큰 단지)는 단지 전체 대신 "건물 동(buildingDong)" 단위로 묶는다 —
  // 동끼리는 멀어도 같은 동 안의 가구는 한 묶음으로 모아 라인/층 정렬을 받게 한다.
  // 동 정보가 없는 가구나 단지명 없는 단독주택은 "" 반환 → 개별(solo) 처리.
  const groupKey = (s: Stop["student"]) => {
    const k = aptKey(s);
    if (!k) return "";
    if (largeComplex.has(k)) {
      const dong = (s.buildingDong || "").trim();
      return dong ? `${k}#동:${dong}` : "";
    }
    return k;
  };

  return { aptKey, largeComplex, groupKey };
}

/** 같은 단지(또는 단독/초대단지=개별)로 묶은 가상 노드. 도로거리 행렬을 노드 단위로 작게 만든다. */
export interface RouteCluster {
  key: string;
  lat: number; // 멤버 중앙(평균) 좌표
  lng: number;
  members: number[]; // stops 인덱스
}

/** stops → 단지 가상 노드 목록. 단독주택/초대단지는 가구 1개짜리 개별 노드. */
export function clusterStops(stops: Stop[]): RouteCluster[] {
  const { groupKey } = buildClusterKeys(stops);
  const map = new Map<string, number[]>();
  stops.forEach((s, idx) => {
    const nodeKey = groupKey(s.student) || `solo:${idx}`;
    (map.get(nodeKey) ?? map.set(nodeKey, []).get(nodeKey)!).push(idx);
  });
  const clusters: RouteCluster[] = [];
  for (const [key, members] of map) {
    let lat = 0, lng = 0;
    for (const i of members) { lat += stops[i].lat; lng += stops[i].lng; }
    clusters.push({ key, lat: lat / members.length, lng: lng / members.length, members });
  }
  return clusters;
}

/**
 * 도로거리 행렬로 단지(클러스터) 순서를 2-opt 최적화한 뒤, 단지 내부는 기존 라인/층 정렬로 채운다.
 * matrix: (M+1)×(M+1). index 0 = origin, 1..M = clusters[i-1]. (방향성 있는 도로거리)
 */
export function optimizeByRoadMatrix(
  stops: Stop[],
  clusters: RouteCluster[],
  matrix: number[][],
  patterns?: Map<string, { pattern: LinePattern }>,
): Stop[] {
  const M = clusters.length;
  if (M === 0) return [];
  const order = optimizeIndices(
    M,
    (i) => matrix[0][i + 1],
    (a, b) => matrix[a + 1][b + 1],
  );
  const emitted: Stop[] = [];
  for (const ci of order) for (const m of clusters[ci].members) emitted.push(stops[m]);
  // 단지 내부 라인/층/형제 정렬은 기존 로직 재사용(클러스터 순서는 이미 인접해 보존됨).
  return packApartmentOrder(emitted, patterns);
}

export function packApartmentOrder(
  stops: Stop[],
  patterns?: Map<string, { pattern: LinePattern }>,
): Stop[] {
  const { aptKey, groupKey } = buildClusterKeys(stops);

  const out: Stop[] = [];
  const used = new Array(stops.length).fill(false);
  for (let i = 0; i < stops.length; i++) {
    if (used[i]) continue;
    const cur = stops[i];
    used[i] = true;

    const key = groupKey(cur.student);
    if (!key) {
      // 단독주택이거나, 초대단지 중 동 정보 없는 가구 → 좌표 최단동선 위치 유지.
      out.push(cur);
      continue;
    }

    // 같은 묶음(일반=단지 전체, 초대단지=같은 동)을 배열 전역에서 끌어모은다.
    const group = [cur];
    for (let j = i + 1; j < stops.length; j++) {
      if (used[j]) continue;
      if (groupKey(stops[j].student) === key) {
        group.push(stops[j]);
        used[j] = true;
      }
    }

    // 패턴은 단지 단위로 지정되므로 동 접미사를 뗀 단지 키로 조회.
    const pattern: LinePattern = patterns?.get(aptKey(cur.student))?.pattern ?? "1-2|3-4";
    group.sort((a, b) => {
      const ad = Number(a.student.buildingDong || 0);
      const bd = Number(b.student.buildingDong || 0);
      if (ad !== bd) return ad - bd;

      // 라인(엘리베이터 코어) 그룹 비교
      const al = classifyLine(a.student, pattern).groupIndex;
      const bl = classifyLine(b.student, pattern).groupIndex;
      if (al !== bl) return al - bl;

      // 같은 라인 내 — 높은 층 먼저
      const ahf = floorOf(a.student.buildingHo);
      const bhf = floorOf(b.student.buildingHo);
      if (ahf !== bhf) return bhf - ahf;
      return Number(a.student.buildingHo || 0) - Number(b.student.buildingHo || 0);
    });
    out.push(...group);
  }
  return out;
}

/**
 * AI가 정한 순서는 버리고, 전체 stop을 글로벌 최단 동선(NN + 2-opt)으로 재배열한다.
 * 법정동·단지 묶음 제약 없이 전체에서 가장 짧은 동선. 같은 건물은 좌표가 같아 연속 방문된다.
 */
export function reorderAiByGeography(origin: { lat: number; lng: number }, stops: Stop[]): Stop[] {
  return globalOptimize(origin, stops);
}

/**
 * 동선 총거리(km) — 오픈 투어: origin → stop0 → stop1 → … → stopN (창고 복귀 없음).
 * 직선거리(Haversine) 합. 같은 메트릭으로 순서끼리 상대 비교하는 용도(계측/진단).
 */
export function tourLengthKm(origin: { lat: number; lng: number }, stops: Stop[]): number {
  if (!stops.length) return 0;
  let total = haversineKm(origin, stops[0]);
  for (let i = 1; i < stops.length; i++) total += haversineKm(stops[i - 1], stops[i]);
  return total;
}

function floorOf(ho: string | undefined): number {
  if (!ho) return 0;
  const s = String(ho).replace(/\D/g, "");
  if (s.length <= 2) return 0;
  return Number(s.slice(0, -2));
}
